import React, { useState, useEffect } from 'react';
import { 
  Settings, 
  Truck, 
  Package, 
  Navigation, 
  Plus, 
  Edit, 
  Trash2,
  Save,
  X,
  Building2,
  FileText,
  DollarSign
} from 'lucide-react';

// Modal pour configurer les éléments de facturation par lignes
const BillingConfigModal = ({ isOpen, item, activeTab, onClose, onSave, currentConfig }) => {
  const [billingLines, setBillingLines] = useState(currentConfig?.lines || []);
  const [newLine, setNewLine] = useState({ group: '', field: '' });

  // Définition des groupes et de leurs champs
  const billingGroups = {
    'TRANSPORT SERVICES': {
      'unloadingLoadingTruck': 'UNLOADING/LOADING TRUCK',
      'onboardDelivery': 'ONBOARD DELIVERY',
      'assistanceToTruckDriver': 'ASSISTANCE TO TRUCK/DRIVER',
      'handlingInOutWh': 'HANDLING IN/OUT WH',
      'truckingAssistance': 'TRUCKING ASSISTANCE'
    },
    'CUSTOMS SERVICES': {
      'customsClearance': 'CUSTOMS CLEARANCE',
      'ist': 'IST',
      'rts': 'RTS',
      'atl': 'ATL',
      'dhlFedex': 'DHL - FEDEX ...'
    },
    'AIRPORT SERVICES': {
      'feeOnArrival': 'FEE ON ARRIVAL',
      'pickingUpForWh': 'PICKING UP FOR WH',
      'pickingUpForDelivery': 'PICKING UP FOR DELIVERY'
    },
    'OTHER SERVICES': {
      'collectionAndTransport': 'COLLECTION AND TRANSPORT',
      'pickUpExpedition': 'PICK UP/EXPEDITION',
      'storageFee': 'STORAGE FEE'
    }
  };

  if (!isOpen || !item) return null;

  const getModalTitle = () => {
    switch (activeTab) {
      case 'transporteurs':
        return `Configuration facturation - Transporteur: ${item.nom}`;
      case 'conditionnements':
        return `Configuration facturation - Conditionnement: ${item.nom}`;
      case 'fabricants':
        return `Configuration facturation - Fabricant: ${item.nom}`;
      case 'fournisseurs-doc-douane':
        return `Configuration facturation - Fournisseur: ${item.nom}`;
      default:
        return `Configuration facturation - ${item.nom}`;
    }
  };

  const handleAddLine = () => {
    if (!newLine.group || !newLine.field) return;
    
    // Vérifier si la ligne existe déjà
    const exists = billingLines.some(line => 
      line.group === newLine.group && line.field === newLine.field
    );
    
    if (exists) {
      alert('Cette ligne de facturation existe déjà.');
      return;
    }

    const newBillingLine = {
      id: Date.now(),
      group: newLine.group,
      field: newLine.field,
      label: billingGroups[newLine.group][newLine.field]
    };

    setBillingLines(prev => [...prev, newBillingLine]);
    setNewLine({ group: '', field: '' });
  };

  const handleRemoveLine = (lineId) => {
    setBillingLines(prev => prev.filter(line => line.id !== lineId));
  };

  const handleSave = () => {
    onSave(item.id, { lines: billingLines });
    onClose();
  };

  const getAvailableFields = () => {
    if (!newLine.group || !billingGroups[newLine.group]) return [];
    return Object.entries(billingGroups[newLine.group]);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-3xl mx-4 max-h-screen overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-port-gray-900">
            {getModalTitle()}
          </h3>
          <button onClick={onClose} className="btn-secondary">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Instructions:</strong> Ajoutez les lignes de facturation qui seront automatiquement 
            pré-cochées dans l'écran "Imprimer la pochette A3" lorsque cet élément est présent dans une saisie.
          </p>
        </div>

        {/* Ajout d'une nouvelle ligne */}
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <h4 className="text-lg font-semibold mb-4 text-port-gray-900">Ajouter une ligne de facturation</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-port-gray-700 mb-2">
                Groupe de services
              </label>
              <select
                value={newLine.group}
                onChange={(e) => setNewLine(prev => ({ ...prev, group: e.target.value, field: '' }))}
                className="input-field"
              >
                <option value="">Sélectionner un groupe</option>
                {Object.keys(billingGroups).map(group => (
                  <option key={group} value={group}>{group}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-port-gray-700 mb-2">
                Service spécifique
              </label>
              <select
                value={newLine.field}
                onChange={(e) => setNewLine(prev => ({ ...prev, field: e.target.value }))}
                className="input-field"
                disabled={!newLine.group}
              >
                <option value="">Sélectionner un service</option>
                {getAvailableFields().map(([field, label]) => (
                  <option key={field} value={field}>{label}</option>
                ))}
              </select>
            </div>
            
            <div className="flex items-end">
              <button
                onClick={handleAddLine}
                disabled={!newLine.group || !newLine.field}
                className="btn-primary w-full flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="h-4 w-4" />
                <span>Ajouter</span>
              </button>
            </div>
          </div>
        </div>

        {/* Liste des lignes de facturation */}
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-4 text-port-gray-900">
            Lignes de facturation configurées ({billingLines.length})
          </h4>
          
          {billingLines.length === 0 ? (
            <div className="text-center py-8 text-port-gray-500 bg-gray-50 rounded-lg">
              <p>Aucune ligne de facturation configurée.</p>
              <p className="text-sm mt-1">Ajoutez-en une ci-dessus.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {billingLines.map((line) => (
                <div
                  key={line.id}
                  className="flex items-center justify-between p-4 border border-port-gray-200 rounded-lg bg-white"
                >
                  <div>
                    <div className="flex items-center space-x-3">
                      <span className="px-3 py-1 text-xs font-medium bg-atl-blue-100 text-atl-blue-800 rounded-full">
                        {line.group}
                      </span>
                      <span className="font-medium text-port-gray-900">
                        {line.label}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveLine(line.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                    title="Supprimer cette ligne"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-6 border-t">
          <button onClick={onClose} className="btn-secondary">
            Annuler
          </button>
          <button 
            onClick={handleSave}
            className="btn-primary flex items-center space-x-2"
          >
            <Save className="h-4 w-4" />
            <span>Sauvegarder la configuration</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const Parametres = () => {
  const [activeTab, setActiveTab] = useState('transporteurs');
  const [transporteurs, setTransporteurs] = useState([
    { id: 1, nom: 'Transport Express', actif: true },
    { id: 2, nom: 'Logistique Pro', actif: true },
    { id: 3, nom: 'Rapid Delivery', actif: false }
  ]);
  const [conditionnements, setConditionnements] = useState([
    { id: 1, nom: 'Carton', actif: true },
    { id: 2, nom: 'Palette', actif: true },
    { id: 3, nom: 'Fût', actif: true },
    { id: 4, nom: 'Sachet', actif: false }
  ]);
  const [typesTransport, setTypesTransport] = useState([
    { id: 1, nom: 'Route', actif: true },
    { id: 2, nom: 'Mer', actif: true },
    { id: 3, nom: 'Air', actif: true },
    { id: 4, nom: 'Rail', actif: false }
  ]);
  const [fabricants, setFabricants] = useState([
    { id: 1, nom: 'Fabricant A', actif: true },
    { id: 2, nom: 'Fabricant B', actif: true },
    { id: 3, nom: 'Fabricant C', actif: false }
  ]);
  const [fournisseursDocDouane, setFournisseursDocDouane] = useState([
    { id: 1, nom: 'Fournisseur Douane 1', actif: true },
    { id: 2, nom: 'Fournisseur Douane 2', actif: true },
    { id: 3, nom: 'Fournisseur Douane 3', actif: false }
  ]);
  const [editingItem, setEditingItem] = useState(null);
  const [newItem, setNewItem] = useState('');
  const [billingModalOpen, setBillingModalOpen] = useState(false);
  const [selectedItemForBilling, setSelectedItemForBilling] = useState(null);
  const [billingConfigs, setBillingConfigs] = useState({});

  const tabs = [
    { id: 'transporteurs', name: 'Transporteurs', icon: Truck },
    { id: 'conditionnements', name: 'Conditionnements', icon: Package },
    { id: 'fabricants', name: 'Fabricants', icon: Building2 },
    { id: 'fournisseurs-doc-douane', name: 'Fournisseurs doc Douane', icon: FileText }
  ];

  const getCurrentList = () => {
    switch (activeTab) {
      case 'transporteurs':
        return { list: transporteurs, setList: setTransporteurs };
      case 'conditionnements':
        return { list: conditionnements, setList: setConditionnements };
      case 'types-transport':
        return { list: typesTransport, setList: setTypesTransport };
      case 'fabricants':
        return { list: fabricants, setList: setFabricants };
      case 'fournisseurs-doc-douane':
        return { list: fournisseursDocDouane, setList: setFournisseursDocDouane };
      default:
        return { list: transporteurs, setList: setTransporteurs };
    }
  };

  const getPlaceholderText = (tabId) => {
    switch (tabId) {
      case 'transporteurs':
        return 'transporteur';
      case 'conditionnements':
        return 'conditionnement';
      case 'types-transport':
        return 'type de transport';
      case 'fabricants':
        return 'fabricant';
      case 'fournisseurs-doc-douane':
        return 'fournisseur doc douane';
      default:
        return 'élément';
    }
  };

  const handleAddItem = () => {
    if (!newItem.trim()) return;
    
    const { list, setList } = getCurrentList();
    const newId = Math.max(...list.map(item => item.id), 0) + 1;
    const newListItem = { id: newId, nom: newItem.trim(), actif: true };
    
    setList([...list, newListItem]);
    setNewItem('');
  };

  const handleEditItem = (item) => {
    setEditingItem({ ...item });
  };

  const handleSaveEdit = () => {
    if (!editingItem || !editingItem.nom.trim()) return;
    
    const { list, setList } = getCurrentList();
    const updatedList = list.map(item => 
      item.id === editingItem.id ? editingItem : item
    );
    
    setList(updatedList);
    setEditingItem(null);
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
  };

  const handleDeleteItem = (id) => {
    const { list, setList } = getCurrentList();
    setList(list.filter(item => item.id !== id));
  };

  const handleToggleActive = (id) => {
    const { list, setList } = getCurrentList();
    const updatedList = list.map(item => 
      item.id === id ? { ...item, actif: !item.actif } : item
    );
    setList(updatedList);
  };

  const handleOpenBillingConfig = (item) => {
    setSelectedItemForBilling(item);
    setBillingModalOpen(true);
  };

  const handleCloseBillingModal = () => {
    setBillingModalOpen(false);
    setSelectedItemForBilling(null);
  };

  const handleSaveBillingConfig = (itemId, config) => {
    const configKey = `${activeTab}_${itemId}`;
    setBillingConfigs(prev => ({
      ...prev,
      [configKey]: config
    }));
    
    // Ici vous pouvez aussi sauvegarder en base de données
    console.log(`Configuration sauvegardée pour ${configKey}:`, config);
  };

  const getBillingConfig = (itemId) => {
    const configKey = `${activeTab}_${itemId}`;
    return billingConfigs[configKey] || {};
  };

  const { list: currentList } = getCurrentList();

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center space-x-3">
        <Settings className="h-8 w-8 text-atl-blue-600" />
        <div>
          <h1 className="text-2xl font-bold text-port-gray-900">Paramètres</h1>
          <p className="text-port-gray-600">Gérez les listes déroulantes de l'application</p>
        </div>
      </div>

      {/* Onglets */}
      <div className="border-b border-port-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-atl-blue-500 text-atl-blue-600'
                    : 'border-transparent text-port-gray-500 hover:text-port-gray-700 hover:border-port-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.name}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Contenu de l'onglet actif */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          {/* Ajout d'un nouvel élément */}
          <div className="flex space-x-3 mb-6">
            <input
              type="text"
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              placeholder={`Ajouter un nouveau ${getPlaceholderText(activeTab)}...`}
              className="flex-1 px-3 py-2 border border-port-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-atl-blue-500 focus:border-transparent"
              onKeyDown={(e) => e.key === 'Enter' && handleAddItem()}
            />
            <button
              onClick={handleAddItem}
              disabled={!newItem.trim()}
              className="px-4 py-2 bg-atl-blue-600 text-white rounded-md hover:bg-atl-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>Ajouter</span>
            </button>
          </div>

          {/* Liste des éléments */}
          <div className="space-y-3">
            {currentList.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between p-4 border border-port-gray-200 rounded-lg hover:bg-port-gray-50"
              >
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-3">
                    {editingItem?.id === item.id ? (
                      <input
                        type="text"
                        value={editingItem.nom}
                        onChange={(e) => setEditingItem({ ...editingItem, nom: e.target.value })}
                        className="px-3 py-1 border border-port-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-atl-blue-500"
                        autoFocus
                      />
                    ) : (
                      <span className={`font-medium ${!item.actif ? 'text-port-gray-400 line-through' : 'text-port-gray-900'}`}>
                        {item.nom}
                      </span>
                    )}
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    item.actif 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {item.actif ? 'Actif' : 'Inactif'}
                  </span>
                </div>

                <div className="flex items-center space-x-2">
                  {editingItem?.id === item.id ? (
                    <>
                      <button
                        onClick={handleSaveEdit}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-md"
                        title="Sauvegarder"
                      >
                        <Save className="h-4 w-4" />
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="p-2 text-port-gray-600 hover:bg-port-gray-50 rounded-md"
                        title="Annuler"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => handleToggleActive(item.id)}
                        className={`p-2 rounded-md ${
                          item.actif 
                            ? 'text-red-600 hover:bg-red-50' 
                            : 'text-green-600 hover:bg-green-50'
                        }`}
                        title={item.actif ? 'Désactiver' : 'Activer'}
                      >
                        {item.actif ? 'Désactiver' : 'Activer'}
                      </button>
                      <button
                        onClick={() => handleOpenBillingConfig(item)}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-md"
                        title="Configuration facturation"
                      >
                        <DollarSign className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEditItem(item)}
                        className="p-2 text-atl-blue-600 hover:bg-atl-blue-50 rounded-md"
                        title="Modifier"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteItem(item.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                        title="Supprimer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {currentList.length === 0 && (
            <div className="text-center py-8 text-port-gray-500">
              Aucun élément dans cette liste. Ajoutez-en un nouveau ci-dessus.
            </div>
          )}
        </div>
      </div>

      {/* Modal de configuration de facturation */}
      <BillingConfigModal
        isOpen={billingModalOpen}
        item={selectedItemForBilling}
        activeTab={activeTab}
        onClose={handleCloseBillingModal}
        onSave={handleSaveBillingConfig}
        currentConfig={selectedItemForBilling ? getBillingConfig(selectedItemForBilling.id) : {}}
      />
    </div>
  );
};

export default Parametres; 